"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { useShell } from "@/components/shell/shell-context";
import { ApiError, api, loginUrl } from "@/lib/api/client";
import type { CostReport, Connection, Project } from "@/lib/api/types";
import { formatCount } from "@/lib/format";
import { monthLabel, percent, shiftMonth } from "@/lib/cost-report";

import { BudgetCard } from "./budget-card";
import { DaysChart } from "./days-chart";
import { JobsTable, KindsTable } from "./usage-tables";
import { SourcesTable } from "./sources-table";

export function CostsView() {
  const { project } = useShell();
  // Bei Projektwechsel neu aufbauen: Monat und Bericht gehören zum Projekt
  return project ? <View key={project.id} project={project} /> : null;
}

function View({ project }: { project: Project }) {
  const router = useRouter();
  const { setConnection } = useShell();
  const [month, setMonth] = useState<string | null>(null); // null = aktueller Monat
  const [report, setReport] = useState<CostReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const load = useCallback(
    (requested: string | null) =>
      api<CostReport>(`/api/costs?project_id=${project.id}${requested ? `&month=${requested}` : ""}`)
        .then((next) => {
          setReport(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 401) {
            router.replace(loginUrl("/kosten"));
            return;
          }
          setError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
        }),
    [project.id, router],
  );

  useEffect(() => {
    void load(month);
  }, [load, month, reload]);

  /** Übernimmt das Ergebnis einer Verbindungsprüfung (Guthaben) in die Tabelle und in die Seitenleiste. */
  const checked = (connection: Connection) => {
    setConnection(connection);
    setReport((current) =>
      current
        ? {
            ...current,
            sources: current.sources.map((s) =>
              s.source === connection.source
                ? {
                    ...s,
                    status: connection.status,
                    status_message: connection.status_message,
                    tested_at: connection.last_tested_at,
                  }
                : s,
            ),
          }
        : current,
    );
  };

  const first = report?.first_month?.slice(0, 7) ?? null;
  const canGoBack = report !== null && first !== null && report.month > first;
  const canGoForward = report !== null && !report.is_current;

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-6 py-7 sm:px-9">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-[13px] text-muted">Kosten · Projekt {project.domain}</div>
          <h1 className="text-3xl font-semibold tracking-tight">Kosten</h1>
        </div>
        <nav aria-label="Monat" className="flex items-center gap-2">
          <button
            type="button"
            disabled={!canGoBack}
            onClick={() => report && setMonth(shiftMonth(report.month, -1))}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-50"
          >
            Vorheriger Monat
          </button>
          <span aria-live="polite" className="min-w-[130px] text-center text-sm font-medium">
            {report ? monthLabel(report.month) : "…"}
          </span>
          <button
            type="button"
            disabled={!canGoForward}
            onClick={() => report && setMonth(shiftMonth(report.month, 1))}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-50"
          >
            Nächster Monat
          </button>
        </nav>
      </header>

      {error && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          <span className="flex-1">Die Kosten konnten nicht geladen werden: {error}</span>
          <button
            type="button"
            onClick={() => setReload((n) => n + 1)}
            className="h-11 rounded-md border border-danger bg-white px-3.5 text-sm"
          >
            Erneut versuchen
          </button>
        </div>
      )}
      {!report && !error && <p className="text-sm text-muted">Kosten werden geladen …</p>}

      {report && (
        <>
          <BudgetCard report={report} />
          <p className="text-[13px] text-muted">
            {formatCount(report.totals.requests)} Abrufe, davon {formatCount(report.totals.cache_hits)} aus dem Cache (
            {percent(report.totals.cache_share)} %). Cache-Treffer kosten nichts.
          </p>
          <SourcesTable report={report} projectId={project.id} onChecked={checked} />
          <KindsTable kinds={report.kinds} />
          <DaysChart days={report.days} />
          <JobsTable jobs={report.jobs} />
        </>
      )}
    </div>
  );
}
